import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing, Colors } from '@/shared/theme';
import { Button, Title, Text, CenteredColumn } from '@/shared/ui';
import { useAnalytics } from '@/hooks';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { useRegistration } from '@/context/RegistrationContext';
import { useDownloadSMS } from '@/hooks/useDownloadSMS';
import { useOtpLink } from '@/hooks/useOtpLink';
import { env } from '@/env';
import { getIsMobilePlatform } from '@/utils/device';
import {
  VIEW_DOWNLOAD_APP,
  DOWNLOAD_APP,
  DOWNLOAD_LINK_INVOKED,
  DOWNLOAD_OTP_LINK_INVOKED,
} from '@/consts/events';

// Assets
import BPTracker from './assets/BPtracker.svg';
import Android from './assets/Android.svg';
import Ios from './assets/Ios.svg';
import HeartIcon from './assets/heart.svg';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: ${Spacing.x2}px;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  width: 100%;

  @media (min-width: 701px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${Spacing.x3}px;
    max-width: 800px;
  }
`;

const TextContent = styled.div`
  display: contents;

  @media (min-width: 701px) {
    display: flex;
    flex-direction: column;
    gap: ${Spacing.x2}px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  order: 0;

  && > * {
    text-align: center;
  }

  @media (min-width: 701px) {
    order: unset;

    && > * {
      text-align: left;
    }
  }
`;

const PhoneImage = styled.img`
  width: 100%;
  max-width: 119px;
  height: auto;
  order: 1;

  @media (min-width: 701px) {
    max-width: 146px;
    order: unset;
  }
`;

const LoginTipRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.x1_5}px;
  order: 2;

  @media (min-width: 701px) {
    order: unset;
  }
`;

const HeartIconImage = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const LoginTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EmailText = styled(Text)`
  text-decoration: underline;
  text-decoration-skip-ink: none;
`;

const StoreBadgesRow = styled.div`
  display: flex;
  gap: ${Spacing.x1_5}px;
  justify-content: center;
  order: 3;

  @media (min-width: 701px) {
    justify-content: flex-start;
    order: unset;
  }
`;

const StoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const StoreBadge = styled.img`
  height: 47px;
  width: auto;
`;

const StickyButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  gap: ${Spacing.x2}px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, #fff 100%);
  padding: ${Spacing.x2}px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  @media (min-width: 701px) {
    position: relative;
    background: none;
    padding: ${Spacing.x3}px 0;
  }
`;

const BottomSpacer = styled.div`
  height: 80px;

  @media (min-width: 701px) {
    display: none;
  }
`;

const DownloadAppStep: React.FC = () => {
  const { trackView, track } = useAnalytics();

  const { t } = useTranslation();
  const { registrationData } = useRegistration();
  const userEmail = registrationData.account.email;
  const { sendDownloadSMS } = useDownloadSMS();
  const { getOtpLink } = useOtpLink();

  // Integrate useAppFlow for flow management
  const { navigateToNextStep } = useAppFlow(STEP_KEYS.DOWNLOAD_APP);

  useEffect(() => {
    trackView({ event: VIEW_DOWNLOAD_APP });
  }, [trackView]);

  const handleDownloadClick = async () => {
    track({ event: DOWNLOAD_APP });

    // Check if on mobile device
    const isMobile = getIsMobilePlatform();

    if (isMobile) {
      // On mobile, open branch link directly

      let link = getOtpLink();
      if (!link) {
        const promoCode = env.ACCESS_PILOT_PROMO_CODE || '';
        const emailParam = userEmail ? `&email=${encodeURIComponent(userEmail)}` : '';
        link = `${env.BRANCH_URL}/?promoCode=${promoCode}${emailParam}`;
        track({ event: DOWNLOAD_LINK_INVOKED });
      } else {
        track({ event: DOWNLOAD_OTP_LINK_INVOKED });
      }

      window.location.href = link;
      track({ event: DOWNLOAD_LINK_INVOKED });
    } else {
      track({ event: DOWNLOAD_OTP_LINK_INVOKED });
      // On desktop, send SMS with download link
      const success = await sendDownloadSMS('submitDownloadApp');
      if (success) {
        navigateToNextStep();
      }
    }
  };

  return (
    <Container>
      <ContentArea>
        <PhoneImage src={BPTracker} alt="" aria-hidden="true" />

        <TextContent>
          <TitleContainer>
            <Title size="h3" as="h1">
              {t('download-app.title-download')}
            </Title>
            <Title size="h2" bold>
              {t('download-app.title-before-monitor')}
            </Title>
          </TitleContainer>

          <LoginTipRow>
            <HeartIconImage src={HeartIcon} alt="" aria-hidden="true" />
            <LoginTextContainer>
              <Text bold>{t('download-app.login-tip-with')}</Text>
              {userEmail && <EmailText data-private>{userEmail}</EmailText>}
            </LoginTextContainer>
          </LoginTipRow>

          <StoreBadgesRow>
            <StoreLink
              onClick={handleDownloadClick}
              aria-label={t('download-app.google-play-aria')}
            >
              <StoreBadge src={Android} alt="Google Play Store" />
            </StoreLink>

            <StoreLink onClick={handleDownloadClick} aria-label={t('download-app.app-store-aria')}>
              <StoreBadge src={Ios} alt="Apple App Store" />
            </StoreLink>
          </StoreBadgesRow>
        </TextContent>
      </ContentArea>

      <StickyButtonContainer>
        <CenteredColumn span={6}>
          <Button
            text={t('download-app.cta-text')}
            onClick={handleDownloadClick}
            variant="brand-primary"
          />
        </CenteredColumn>
      </StickyButtonContainer>

      <BottomSpacer />
    </Container>
  );
};

export { DownloadAppStep };
